import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Calendar, Clock, AlertTriangle, Info } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate } from '../../utils/dateUtils';

const Employee360LeaveView = ({ employeeId }) => {
  const { hasPermission } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLeaveSummary = async () => {
      if (!employeeId) {
        setSummary(null);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);
      setSummary(null); // Clear previous employee's leave data

      try {
        const res = await axios.get(`/v1/leaves/employee/${employeeId}/summary`);
        setSummary(res.data);
      } catch (err) {
        console.error('Failed to fetch employee leave summary:', err);
        setError('Unable to load leave information.');
      } finally {
        setLoading(false);
      }
    };

    fetchLeaveSummary();
  }, [employeeId]);

  if (!hasPermission('emp:leave:view')) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 flex items-center space-x-2">
        <AlertTriangle className="w-5 h-5 flex-shrink-0" />
        <span>You do not have permission to view leave balances.</span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8 text-sm font-semibold text-gray-500">
        Loading employee leave profile...
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 flex items-center space-x-2">
        <AlertTriangle className="w-5 h-5 flex-shrink-0" />
        <span>{error}</span>
      </div>
    );
  }

  if (!summary) {
    return null;
  }

  const { policy, balances, applications } = summary;

  return (
    <div className="space-y-6">
      {/* SECTION 1: Leave Policy */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-4">
        <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center space-x-2">
          <Info className="w-4 h-4 text-blue-600" />
          <span>Leave Policy Details</span>
        </h3>
        {policy ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-xs font-semibold">
            <div>
              <p className="text-gray-500 uppercase tracking-wide">Assigned Policy</p>
              <p className="text-gray-900 font-bold text-sm mt-0.5">{policy.name}</p>
            </div>
            <div>
              <p className="text-gray-500 uppercase tracking-wide">Status</p>
              <p className="mt-0.5">
                <span className={`inline-flex px-2 py-0.5 rounded-full font-bold uppercase tracking-wider text-[10px] ${policy.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                  {policy.status}
                </span>
              </p>
            </div>
            <div>
              <p className="text-gray-500 uppercase tracking-wide">Effective From</p>
              <p className="text-gray-900 font-semibold text-sm mt-0.5">{formatDate(policy.effective_from)}</p>
            </div>
            <div>
              <p className="text-gray-500 uppercase tracking-wide">Effective To</p>
              <p className="text-gray-900 font-semibold text-sm mt-0.5">
                {formatDate(policy.effective_to, 'Ongoing')}
              </p>
            </div>
          </div>
        ) : (
          <div className="rounded-lg border border-yellow-250 bg-yellow-50/50 p-4 text-sm text-yellow-800 flex items-center space-x-2">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-bold">⚠ No Leave Policy Assigned</p>
              <p className="text-xs text-yellow-750 mt-0.5">Please assign a leave policy to this employee.</p>
            </div>
          </div>
        )}
      </div>

      {/* SECTION 2: Leave Balance */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-4">
        <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center space-x-2">
          <Calendar className="w-4 h-4 text-blue-600" />
          <span>Leave Balance Summary</span>
        </h3>
        {balances && balances.length > 0 ? (
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 text-xs font-semibold">
              <thead className="bg-gray-50 text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-5 py-3 text-left">Leave Type</th>
                  <th className="px-5 py-3 text-left">Period</th>
                  <th className="px-5 py-3 text-right">Entitled</th>
                  <th className="px-5 py-3 text-right">Consumed</th>
                  <th className="px-5 py-3 text-right">Remaining</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100 text-gray-950">
                {balances.map((b) => (
                  <tr key={b.leave_type_id + '-' + b.period_type} className="hover:bg-gray-50/50">
                    <td className="px-5 py-3 font-bold">{b.name} ({b.code})</td>
                    <td className="px-5 py-3">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] bg-blue-100 text-blue-800 font-bold">
                        {b.period_type}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right font-semibold">{b.entitled}</td>
                    <td className="px-5 py-3 text-right font-semibold">{b.consumed}</td>
                    <td className="px-5 py-3 text-right font-bold text-blue-700">{b.remaining}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center p-6 text-xs text-gray-500 border border-dashed rounded-lg">
            No leave balance records found.
          </div>
        )}
      </div>

      {/* SECTION 3: Recent Leave Applications */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm space-y-4">
        <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center space-x-2">
          <Clock className="w-4 h-4 text-blue-600" />
          <span>Recent Leave Applications</span>
        </h3>
        {applications && applications.length > 0 ? (
          <div className="overflow-x-auto border border-gray-200 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200 text-xs font-semibold">
              <thead className="bg-gray-50 text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="px-5 py-3 text-left">Applied On</th>
                  <th className="px-5 py-3 text-left">Duration</th>
                  <th className="px-5 py-3 text-left">Leave Type</th>
                  <th className="px-5 py-3 text-right">Days</th>
                  <th className="px-5 py-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100 text-gray-950">
                {applications.map((app) => (
                  <tr key={app.id} className="hover:bg-gray-50/50">
                    <td className="px-5 py-3 text-gray-500">{formatDate(app.created_at)}</td>
                    <td className="px-5 py-3 font-semibold">
                      {formatDate(app.from_date)} → {formatDate(app.to_date)}
                    </td>
                    <td className="px-5 py-3">{app.leave_type_name} ({app.leave_type_code})</td>
                    <td className="px-5 py-3 text-right font-bold">{app.no_of_days}</td>
                    <td className="px-5 py-3 text-center">
                      <span className={`inline-flex px-2.5 py-0.5 rounded-full font-bold uppercase tracking-wider text-[10px] ${
                        app.status === 'hq_approved' ? 'bg-green-100 text-green-800' :
                        app.status === 'rejected' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {app.status === 'hq_approved' ? 'Approved' : app.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center p-6 text-xs text-gray-500 border border-dashed rounded-lg">
            No leave applications found.
          </div>
        )}
      </div>
    </div>
  );
};

export default Employee360LeaveView;
